import { z } from "zod";

export const createMedicationSchema = z.object({
  name: z.string().min(1, "Medication name is required").max(200, "Name cannot exceed 200 characters").trim(),
  batchNumber: z.string().min(1, "Batch number is required").max(100).trim().optional(),
  expiryDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Expiry date must be in YYYY-MM-DD format")
    .refine((value) => !Number.isNaN(new Date(value).getTime()), "Expiry date must be a valid date"),
  quantity: z.number().int("Quantity must be a whole number").min(0, "Quantity cannot be negative").max(1_000_000),
  unit: z.string().max(50).trim().optional(),
  category: z.string().max(100).trim().optional(),
  location: z.string().max(200).trim().optional(),
  notes: z.string().max(1000, "Notes cannot exceed 1000 characters").trim().optional(),
});

export const updateMedicationSchema = createMedicationSchema.partial().extend({
  id: z.string().min(1, "Medication ID is required"),
});

export const deleteMedicationSchema = z.object({
  id: z.string().min(1, "Medication ID is required"),
});

export const pillCountSubmitSchema = z.object({
  medicationId: z.string().min(1, "Medication ID is required"),
  countedQuantity: z.number().int("Count must be a whole number").min(0, "Count cannot be negative").max(10_000),
  confirmedByUser: z.boolean().refine((value) => value === true, "Human confirmation is required before submitting count"),
  sessionId: z.string().uuid("Session ID must be a valid UUID").optional(),
  sourceType: z.enum(["manual", "ai_assisted", "camera"]).default("manual"),
  timestamp: z.string().datetime({ offset: true }).optional(),
});

export const pillCountUsageSchema = z.object({
  action: z.enum(["increment", "check"]),
  userId: z.string().min(1).optional(),
});

export const updateProfileSchema = z.object({
  displayName: z.string().min(1, "Display name is required").max(100).trim().optional(),
  organizationName: z.string().max(200).trim().optional(),
  email: z.string().email("Must be a valid email address").optional(),
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(128, "Password cannot exceed 128 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string(),
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export const billingCheckoutSchema = z.object({
  plan: z.enum(["pro", "premium"]),
  returnTo: z.enum(["dashboard", "manage-subscription", "pricing"]).optional(),
});

export const emailSubscriptionSchema = z.object({
  subscriberEmail: z.string().email("Must be a valid email address").max(320).trim(),
});

export const newsletterOptInSchema = z.object({
  newsletter_opt_in: z.boolean().optional(),
});

export const parseStockSheetSchema = z.object({
  base64: z.string().min(1, "Missing base64"),
  mediaType: z.string().min(1).max(200).optional(),
  /** Original filename helps detect XLSX / PDF / DOCX vs a raw image upload. */
  fileName: z.string().max(512).optional(),
});

export const parseBatchPairSchema = z
  .object({
    labelBase64: z.string().min(1, "label image required"),
    labelMediaType: z.string().min(1).max(120).optional(),
    expiryBase64: z.string().min(1).optional(),
    expiryMediaType: z.string().min(1).max(120).optional(),
    expiryMonth: z.number().int().min(1).max(12).optional(),
    expiryYear: z.number().int().min(2000).max(2100).optional(),
  })
  .refine(
    (value) =>
      (Boolean(value.expiryBase64) && !value.expiryMonth && !value.expiryYear) ||
      (!value.expiryBase64 && typeof value.expiryMonth === "number" && typeof value.expiryYear === "number"),
    "Provide either expiry photo or expiry month/year",
  );

export const parseBatchScanSchema = z.object({
  pairs: z.array(parseBatchPairSchema).min(1, "No image pairs supplied"),
  labelExtractV2: z.boolean().optional(),
});

/** Human-verified OCR / model misread → canonical product name (global, all users). */
export const learnedNameCorrectionSchema = z.object({
  wrongName: z.string().min(1, "wrongName is required").max(220).trim(),
  correctName: z.string().min(1, "correctName is required").max(220).trim(),
});

/** Confirmed shelf count after AI pill scan (feeds improvement datasets). */
export const pillCountFeedbackSchema = z.object({
  scanId: z.number().int().positive(),
  humanCount: z.number().int().min(0).max(1_000_000),
  aiCount: z.number().int().min(0).max(1_000_000).optional().nullable(),
});
